import { useState } from 'react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { PartnerFormModal } from '@/components/Partners/Modals/PartnerFormModal'
import { PartnerDetailModal } from '@/components/Partners/Modals/PartnerDetailModal'
import { PartnersStatsCards } from '@/components/Partners/PartnersStatsCards'
import { PartnersTable } from '@/components/Partners/PartnersTable'
import { PartnersPyramidView } from '@/components/Partners/PartnersPyramidView'
import { PartnersPaymentsView } from '@/components/Partners/Payments/PartnersPaymentsView'
import { PartnerCadastroLinkButton } from '@/components/Partners/PartnerCadastroLinkButton'
import { Search, Plus, List, Network, Wallet, ShieldCheck } from 'lucide-react'
import { usePartnersPage } from '@/hooks/usePartnersPage'
import { useAuthStore } from '@/stores/authStore'
import { isRocketInstance } from '@/config/instanceProfile'
import { cn } from '@/utils/cn'
import {
  canDeleteParceiros,
  canManagePartnerCommissions,
  canRegisterPartnerPayments,
  canWriteParceiros,
} from '@/utils/permissions'

type PartnersView = 'list' | 'pyramid' | 'payments'

export default function Partners() {
  const userRole = useAuthStore((s) => s.user?.role)
  const canWrite = canWriteParceiros(userRole)
  const canDelete = canDeleteParceiros(userRole)
  const canManageCommissions = canManagePartnerCommissions(userRole)
  const canRegisterPayments = canRegisterPartnerPayments(userRole)
  const [view, setView] = useState<PartnersView>('list')

  const {
    partners,
    loading,
    search,
    setSearch,
    showFormModal,
    setShowFormModal,
    formData,
    setFormData,
    saving,
    selectedPartner,
    setSelectedPartner,
    filtered,
    sparklineData,
    sparklineToPay,
    sparklinePaid,
    handleSave,
    handleDelete,
    handleApprovePortalAccess,
    openForm,
    openDetail,
    handleUpdateCommissionStatus,
    handleCalculateCommission,
    handleAddPayment,
    handleRecurrenceAction,
    pyramidVersion,
    refreshPartners,
    comerciais,
  } = usePartnersPage()

  const openDetailById = (partnerId: string) => {
    const partner = partners.find((p) => p.id === partnerId)
    if (partner) openDetail(partner)
  }
  const pendingValidationCount = partners.filter((partner) => !partner.portalUserId).length

  return (
    <DashboardLayout title='Partners'>
      {userRole?.toLowerCase() === 'closer' && (
        <p className='mb-4 text-sm text-[var(--font-color-muted)]'>
          Você vê somente os parceiros vinculados ao seu nome. Indicações, contato e prêmio ficam dentro de cada parceiro.
        </p>
      )}

      {pendingValidationCount > 0 && (
        <div className='mb-4 flex items-start gap-3 rounded-2xl border border-amber-500/20 bg-amber-500/10 p-4 text-amber-100'>
          <div className='mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-500/15 text-amber-300'>
            <ShieldCheck size={18} />
          </div>
          <div>
            <p className='text-sm font-bold'>
              {pendingValidationCount} {pendingValidationCount === 1 ? 'parceiro aguardando validação' : 'parceiros aguardando validação'}
            </p>
            <p className='mt-1 text-xs text-amber-100/75'>
              Revise os dados na tabela e clique em Liberar acesso para ativar o portal e enviar o e-mail ao parceiro.
            </p>
          </div>
        </div>
      )}

      <PartnersStatsCards
        partners={partners}
        sparklineData={sparklineData}
        sparklineToPay={sparklineToPay}
        sparklinePaid={sparklinePaid}
      />

      <div className='glass-card mb-4 flex items-center gap-3'>
        <div className='flex-1'>
          <Input
            placeholder='Buscar parceiro por nome, empresa ou email...'
            icon={<Search size={14} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {isRocketInstance && (
          <div className='flex shrink-0 rounded-lg border border-[var(--border)] p-0.5' role='tablist' aria-label='Visualização'>
            {[
              { id: 'list' as const, label: 'Lista', icon: List },
              { id: 'pyramid' as const, label: 'Pirâmide', icon: Network },
              ...(canManageCommissions ? [{ id: 'payments' as const, label: 'Pagamentos', icon: Wallet }] : []),
            ].map((option) => (
              <button
                key={option.id}
                type='button'
                role='tab'
                aria-selected={view === option.id}
                onClick={() => setView(option.id)}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                  view === option.id
                    ? 'bg-[var(--brand-primary)] text-white'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
                )}
              >
                <option.icon size={13} />
                {option.label}
              </button>
            ))}
          </div>
        )}
        {isRocketInstance && <PartnerCadastroLinkButton />}
        {canWrite && (
          <Button variant='primary' size='sm' className='shrink-0' icon={<Plus size={13} />} onClick={() => openForm()}>
            Novo Parceiro
          </Button>
        )}
      </div>

      {view === 'payments' && isRocketInstance && canManageCommissions ? (
        <PartnersPaymentsView
          version={pyramidVersion}
          search={search}
          canApprove={canManageCommissions}
          canPay={canRegisterPayments}
          onChanged={refreshPartners}
          onOpenPartner={openDetailById}
        />
      ) : view === 'pyramid' && isRocketInstance ? (
        <PartnersPyramidView version={pyramidVersion} search={search} onSelect={openDetailById} />
      ) : (
        <PartnersTable
          partners={filtered}
          loading={loading}
          search={search}
          onSelect={openDetail}
          onEdit={canWrite ? openForm : undefined}
          onDelete={canDelete ? handleDelete : undefined}
          onValidatePortalAccess={openDetail}
        />
      )}

      <PartnerDetailModal
        partner={selectedPartner}
        onClose={() => setSelectedPartner(null)}
        onUpdateCommissionStatus={handleUpdateCommissionStatus}
        onCalculateCommission={handleCalculateCommission}
        onAddPayment={handleAddPayment}
        onRecurrenceAction={canManageCommissions ? handleRecurrenceAction : undefined}
        onSelectPartner={openDetailById}
        onApprovePortalAccess={handleApprovePortalAccess}
        onEdit={canWrite ? openForm : undefined}
      />

      <PartnerFormModal
        open={showFormModal}
        onClose={() => setShowFormModal(false)}
        formData={formData}
        setFormData={setFormData}
        onSave={handleSave}
        saving={saving}
        partners={partners}
        comerciais={comerciais}
      />
    </DashboardLayout>
  )
}
